import {Observable} from 'rxjs';
import {APIResponse, SimpleUnit, ThresholdStatic} from '@core/interfaces/system/system-common';
import {SensitivityConfiguration} from '@store/load-forecast/sensitivity-analysis.store';
import {InfrastructureType} from '@core/interfaces/engin/load-profile-analysis/lp-common.model';
import {PeriodFilter} from '@core/interfaces/engin/load-forecast/load-forecast';

/*
 * Response
 */
export interface FeederList {
    feeders: FeederLoadData[];
}
export interface FeederLoadData {
    feeder: FeederData;
    load: YearlyLoadData[];
    loadUnit: SimpleUnit;
}
export interface FeederData extends FeederIdName, TransformerIdName, StationIdName {
    capacity: number;
    capacityUnit: SimpleUnit;
    peakingSeasonId: string;
}

export interface TransformerList {
    transformers: TransformerLoadData[];
}
export interface TransformerLoadData {
    transformer: TransformerData;
    load: YearlyLoadData[];
    loadUnit: SimpleUnit;
}
export interface TransformerDetailedList {
    transformers: TransformerLoadDetailedData[];
}
export interface TransformerLoadDetailedData {
    transformer: TransformerData;
    load: TransformerResultBreakdown[];
    loadUnit: SimpleUnit;
}
export interface TransformerResultBreakdown {
    year: number;
    originalLoad: number;
    finalLoad: number;
    nonCoincidentTransferOut: number;
    nonCoincidentTransferIn: number;
    coincidentNetTransfer: number;
    diversityFactor: number;
}
export interface TransformerData extends TransformerIdName, StationIdName {
    busId: string;
    capacity: number;
    capacityUnit: SimpleUnit;
    peakingSeasonId: string;
}

export interface TransformerAnalysis {
    feederTies: TransformerFeederTie[];
    tieStatusLabels: TieStatusLabel[];
}
export interface TransformerFeederTie extends FeederIdName, TransformerIdName {
    results: TransformerAnalysisTieResult[];
    loadUnit: SimpleUnit;
    capacity: number;
    capacityUnit: SimpleUnit;
}
export interface TransformerAnalysisTieResult {
    year: number;
    loadToTransfer: number;
    feederTieStatus: string;
    feederTieUsed?: TransformerAnalysisTieUsed;
}
export interface TransformerAnalysisTieUsed extends FeederIdName, TransformerIdName {
    // Feeder
    currentLoad: number;
    loadAfterTransfer: number;
    // Transformer
    transformerCurrentLoad: number;
    transformerCapacity: number;
    transformerDiversityFactor: number;
    // Transfer
    nonCoincidentTransfer: number;
    coincidentTransfer: number;
}
export interface TieStatusLabel {
    tieStatus: string;
    label: string; // 256 chars
    labelShort: string; // 50 chars
}

export interface FeederAnalysisImpact {
    feeder: FeederLoadData;
    feederTies: FeederLoadData[];
    feederTieTransformers: TransformerLoadData[];
    tieAwayLoad: YearlyLoadData[];
    loadAtRisk: YearlyLoadData[];
    tieFeedersUsed: YearlyTieFeedersUsed[];
}
export interface TransformerLoadData {
    transformer: TransformerData;
    load: YearlyLoadData[];
    loadUnit: SimpleUnit;
}

export interface TransformerImpactSummary {
    transformer: TransformerLoadData;
    sameBusTransformers: TransformerLoadDetailedData[];
    tieAwayLoad: TieAwayLoadDetail[];
    overload: YearlyLoadData[];
}
export interface TieAwayLoadDetail {
    year: number;
    nonCoincidentLoad: number;
    coincidentLoadRemoved: number;
    coincidentLoadAdded: number;
}

/*
 * Request
 */
export interface N1AnalysisDetailsRequest {
    infrastructureType: string;
    infrastructureId: string;
    seasonId: string;
    scenario: string;
    period: PeriodFilter;
    unit: string;
}
/*
 * Supporting DTOs
 */
export interface YearlyLoadData {
    year: number;
    amount: number;
}

export interface YearlyTieFeedersUsed {
    year: number;
    feederIds: string[];
}

export interface FeederIdName {
    feederId: string;
    feederName: string;
}

export interface TransformerIdName {
    transformerId: string;
    transformerName: string;
}

export interface StationIdName {
    stationId: string;
    stationName: string;
}

export abstract class N1AnalysisService {
    abstract getFeedersLoadData(
        workflowItemId: number,
        params: N1AnalysisDetailsRequest,
    ): Observable<APIResponse<FeederList>>;

    abstract getTransformersLoadData(
        workflowItemId: number,
        params: N1AnalysisDetailsRequest,
    ): Observable<APIResponse<TransformerList>>;

    abstract getTransformerFeederTieResults(
        workflowItemId: number,
        params: N1AnalysisDetailsRequest,
    ): Observable<APIResponse<TransformerAnalysis>>;

    abstract getFeederTieResults(
        workflowItemId: number,
        params: N1AnalysisDetailsRequest,
    ): Observable<APIResponse<FeederAnalysisImpact>>;

    abstract getTransformerImpact(
        workflowItemId: number,
        params: N1AnalysisDetailsRequest,
    ): Observable<APIResponse<TransformerDetailedList>>;

    abstract getTransformerImpactSummary(
        workflowItemId: number,
        params: N1AnalysisDetailsRequest,
    ): Observable<APIResponse<TransformerImpactSummary>>;
}
